/**
 * Balance + latest transactions for a mainnet address, from POST /api/address_lookup
 * (app/modules/AddressLookup.rb). The server fetches and caches it; the browser never
 * calls a block explorer directly.
 */
export interface LookupTx {
  txid: string;
  confirmed: boolean;
  block_height: number | null;
  block_time: number | null;
  confirmations: number;
  delta_sats: number;
  fee_sats: number;
  fee_rate: number | null;
  /** what this address received / spent in this tx (absent in results cached before v2) */
  received_sats?: number;
  sent_sats?: number;
  /** USD per BTC when it confirmed; null before July 2025 or while unconfirmed */
  btc_usd_then?: number | null;
}

export interface LookupPrivacy {
  /** payments (outputs) this address has received, confirmed + mempool */
  receive_count: number;
  received_after_spend: boolean;
  /** latest txs that sent it a tiny amount without spending from it */
  dust_tx_count: number;
  dust_max_sats: number;
}

export interface LookupUtxo {
  txid: string;
  vout: number;
  value: number;
  confirmed: boolean;
}

export interface LookupUtxos {
  /** ok, too_many (the data service won't list them) or unavailable */
  status: "ok" | "too_many" | "unavailable";
  type: string;
  count: number;
  input_vbytes: number;
  output_vbytes: number;
  dust_limit: number;
  /** set when input_vbytes depends on the script */
  estimate: string | null;
  /** every UTXO value, largest first (status ok) */
  values?: number[];
  /** the largest 50 (status ok) */
  list?: LookupUtxo[];
}

/** live fee tiers in sat/vB (decimal) */
export interface LookupFees {
  fastest: number;
  half_hour: number;
  hour: number;
  economy: number;
  minimum: number;
}

export interface AddressLookupResult {
  address: string;
  source: string;
  fetched_at: string;
  tip_height: number;
  btc_usd_price: number | null;
  confirmed: {
    balance_sats: number;
    received_sats: number;
    sent_sats: number;
    tx_count: number;
  };
  unconfirmed: {
    incoming_sats: number;
    outgoing_sats: number;
    tx_count: number;
  };
  txs: LookupTx[];
  more_txs: boolean;
  privacy?: LookupPrivacy;
  utxos?: LookupUtxos;
  fees?: LookupFees | null;
}

/** result on success; otherwise error (and login: true when logging in lifts the limit) */
export interface LookupResponse {
  result: AddressLookupResult | null;
  error: string | null;
  login: boolean;
  /** the server's status on errors: limited, busy, invalid, unavailable */
  status: string | null;
}

/** Mainnet types the server can look up (testnet and invalid ones are never sent). */
export const LOOKUP_TYPES = new Set([
  "bitcoin_legacy_p2pkh",
  "bitcoin_legacy_p2sh",
  "bitcoin_segwit_v0",
  "bitcoin_segwit_p2wsh",
  "bitcoin_taproot",
]);

export async function lookupAddress(address: string): Promise<LookupResponse> {
  try {
    const response = await fetch("/api/address_lookup", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token":
          document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute("content") ?? "",
      },
      body: JSON.stringify({ address }),
    });
    const data = await response.json().catch(() => ({}));
    if (response.ok) {
      return { result: data as AddressLookupResult, error: null, login: false, status: null };
    }
    return {
      result: null,
      error:
        data.error ||
        "We couldn't look up this address right now. Please try again in a minute.",
      login: data.login === true,
      status: typeof data.status === "string" ? data.status : null,
    };
  } catch {
    return {
      result: null,
      error:
        "We couldn't look up this address right now. Please try again in a minute.",
      login: false,
      status: null,
    };
  }
}

export const formatSats = (sats: number): string =>
  `${Math.round(sats).toLocaleString("en-US")} sats`;

export const formatBtc = (sats: number): string => {
  const btc = (sats / 100_000_000).toFixed(8).replace(/\.?0+$/, "");
  return `${btc} BTC`;
};

export const formatUsd = (sats: number, price: number | null): string | null => {
  if (!price) return null;
  const usd = (sats / 100_000_000) * price;
  return usd.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: Math.abs(usd) < 1 ? 4 : 2,
  });
};
